import { useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { Modal } from "./ui.js";

export type QuickLogType =
  | "walk"
  | "water"
  | "sleep"
  | "pain"
  | "medication"
  | "recovery"
  | "readiness"
  | "weight"
  | "measurement"
  | "hunger";

const TITLES: Record<QuickLogType, string> = {
  walk: "Log a walk",
  water: "Log water",
  sleep: "Log sleep",
  pain: "Log pain",
  medication: "Log medication",
  recovery: "Recovery check-in",
  readiness: "Daily readiness",
  weight: "Log weight",
  measurement: "Log a measurement",
  hunger: "Log hunger / craving"
};

const MEASUREMENT_SITES: [string, string][] = [
  ["waist", "Waist"],
  ["chest", "Chest"],
  ["shoulders", "Shoulders"],
  ["neck", "Neck"],
  ["left_arm", "Left arm"],
  ["right_arm", "Right arm"],
  ["hips", "Hips"],
  ["left_thigh", "Left thigh"],
  ["right_thigh", "Right thigh"],
  ["left_calf", "Left calf"],
  ["right_calf", "Right calf"]
];

export function QuickLogModal({
  type,
  onClose,
  onLogged
}: {
  type: QuickLogType;
  onClose: () => void;
  onLogged?: () => void;
}) {
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set(key: string, value: unknown) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      // Always stamp the user's LOCAL date so "today" is consistent between
      // logging and the pages that query by date (the server otherwise defaults
      // a missing date to its own UTC date, which can differ by a day).
      await api.post(ENDPOINT[type], { date: localDate(), ...(buildPayload(type, form) as Record<string, unknown>) });
      onLogged?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      setSaving(false);
    }
  }

  return (
    <Modal title={TITLES[type]} onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      {renderFields(type, form, set)}
      <div className="row mt-4">
        <button className="btn btn-primary grow" onClick={submit} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="btn" onClick={onClose} disabled={saving}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

const ENDPOINT: Record<QuickLogType, string> = {
  walk: "/api/log/walk",
  water: "/api/log/water",
  sleep: "/api/log/sleep",
  pain: "/api/log/pain",
  medication: "/api/log/medication",
  recovery: "/api/recovery/checkin",
  readiness: "/api/today/readiness",
  weight: "/api/body/weight",
  measurement: "/api/body/measurements",
  hunger: "/api/log/hunger"
};

function num(v: unknown): number | null {
  if (v === "" || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function localDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function buildPayload(type: QuickLogType, f: Record<string, unknown>): unknown {
  switch (type) {
    case "walk":
      return {
        durationMinutes: num(f.durationMinutes),
        steps: num(f.steps),
        perceivedDifficulty: num(f.perceivedDifficulty),
        discomfort: Boolean(f.discomfort),
        notes: (f.notes as string) || null
      };
    case "water":
      return { amountOz: num(f.amountOz) ?? 0 };
    case "sleep":
      return {
        durationHours: num(f.durationHours),
        quality: num(f.quality),
        notes: (f.notes as string) || null
      };
    case "pain":
      return {
        area: (f.area as string) || null,
        severity: num(f.severity),
        suspectedCause: (f.suspectedCause as string) || null,
        note: (f.note as string) || null
      };
    case "medication":
      return { notes: (f.notes as string) || null, skipped: Boolean(f.skipped) };
    case "recovery":
      return {
        pain: num(f.pain),
        energy: num(f.energy),
        nausea: Boolean(f.nausea),
        walkingComfort: num(f.walkingComfort),
        incisionState: (f.incisionState as string) || null,
        notes: (f.notes as string) || null
      };
    case "readiness":
      return {
        sleepQuality: num(f.sleepQuality),
        energy: num(f.energy),
        soreness: num(f.soreness),
        stress: num(f.stress),
        pain: num(f.pain),
        notes: (f.notes as string) || null
      };
    case "weight":
      return { weightLb: num(f.weightLb) ?? 0 };
    case "measurement":
      return { site: (f.site as string) || "waist", valueIn: num(f.valueIn) ?? 0 };
    case "hunger":
      return { hunger: num(f.hunger), cravingType: (f.cravingType as string) || null, note: (f.note as string) || null };
  }
}

function NumberField({
  label,
  k,
  form,
  set,
  unit,
  min,
  max,
  step
}: {
  label: string;
  k: string;
  form: Record<string, unknown>;
  set: (k: string, v: unknown) => void;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="row">
        <input
          className="input"
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step ?? 1}
          value={(form[k] as string) ?? ""}
          onChange={(e) => set(k, e.target.value)}
          style={{ maxWidth: 160 }}
        />
        {unit && <span className="dim">{unit}</span>}
      </div>
    </div>
  );
}

function ScaleField({
  label,
  k,
  form,
  set,
  min = 0,
  max = 10
}: {
  label: string;
  k: string;
  form: Record<string, unknown>;
  set: (k: string, v: unknown) => void;
  min?: number;
  max?: number;
}) {
  const items = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div className="field">
      <label>{label}</label>
      <div className="scale-row" style={{ flexWrap: "wrap" }}>
        {items.map((n) => (
          <button
            key={n}
            type="button"
            className={`scale-dot${form[k] === n ? " selected" : ""}`}
            style={{ width: 40, height: 40 }}
            onClick={() => set(k, n)}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function NotesField({
  form,
  set,
  k = "notes",
  label = "Notes"
}: {
  form: Record<string, unknown>;
  set: (k: string, v: unknown) => void;
  k?: string;
  label?: string;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <textarea
        className="textarea"
        rows={2}
        value={(form[k] as string) ?? ""}
        onChange={(e) => set(k, e.target.value)}
      />
    </div>
  );
}

function renderFields(
  type: QuickLogType,
  form: Record<string, unknown>,
  set: (k: string, v: unknown) => void
) {
  switch (type) {
    case "walk":
      return (
        <>
          <NumberField label="Duration" k="durationMinutes" unit="min" form={form} set={set} min={0} max={600} />
          <NumberField label="Steps (optional)" k="steps" form={form} set={set} min={0} />
          <ScaleField label="How hard did it feel? (1–10)" k="perceivedDifficulty" form={form} set={set} min={1} max={10} />
          <NotesField form={form} set={set} />
        </>
      );
    case "water":
      return <NumberField label="Amount" k="amountOz" unit="oz" form={form} set={set} min={0} max={400} />;
    case "sleep":
      return (
        <>
          <NumberField label="Hours slept" k="durationHours" unit="hrs" form={form} set={set} min={0} max={24} step={0.5} />
          <ScaleField label="Quality (1–5)" k="quality" form={form} set={set} min={1} max={5} />
          <NotesField form={form} set={set} />
        </>
      );
    case "pain":
      return (
        <>
          <div className="field">
            <label>Area</label>
            <input className="input" value={(form.area as string) ?? ""} onChange={(e) => set("area", e.target.value)} placeholder="e.g. lower back, feet" />
          </div>
          <ScaleField label="Severity (0–10)" k="severity" form={form} set={set} min={0} max={10} />
          <div className="field">
            <label>What do you suspect? (optional)</label>
            <input className="input" value={(form.suspectedCause as string) ?? ""} onChange={(e) => set("suspectedCause", e.target.value)} />
            <span className="hint">Kept as your suspicion — never recorded as a diagnosis.</span>
          </div>
          <NotesField form={form} set={set} k="note" />
        </>
      );
    case "medication":
      return (
        <>
          <p className="muted text-sm">Mark that you took your medications today.</p>
          <NotesField form={form} set={set} label="Notes (optional)" />
        </>
      );
    case "recovery":
      return (
        <>
          <ScaleField label="Pain (0–10)" k="pain" form={form} set={set} min={0} max={10} />
          <ScaleField label="Energy (0–10)" k="energy" form={form} set={set} min={0} max={10} />
          <ScaleField label="Walking comfort (0–10)" k="walkingComfort" form={form} set={set} min={0} max={10} />
          <div className="field">
            <label>Nausea?</label>
            <div className="choice-grid">
              {[["No", false], ["Yes", true]].map(([l, v]) => (
                <button key={String(l)} type="button" className={`choice${form.nausea === v ? " selected" : ""}`} onClick={() => set("nausea", v)}>
                  {l as string}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <label>Incision / recovery notes (optional)</label>
            <textarea className="textarea" rows={2} value={(form.incisionState as string) ?? ""} onChange={(e) => set("incisionState", e.target.value)} placeholder="For your own comparison and to discuss with your clinician." />
          </div>
          <NotesField form={form} set={set} />
        </>
      );
    case "readiness":
      return (
        <>
          <ScaleField label="Sleep quality (1–5)" k="sleepQuality" form={form} set={set} min={1} max={5} />
          <ScaleField label="Energy (1–5)" k="energy" form={form} set={set} min={1} max={5} />
          <ScaleField label="Soreness (1–5)" k="soreness" form={form} set={set} min={1} max={5} />
          <ScaleField label="Stress (1–5)" k="stress" form={form} set={set} min={1} max={5} />
          <NotesField form={form} set={set} />
        </>
      );
    case "weight":
      return <NumberField label="Weight" k="weightLb" unit="lb" form={form} set={set} min={40} max={1200} step={0.1} />;
    case "measurement":
      return (
        <>
          <div className="field">
            <label>Where</label>
            <select className="select" value={(form.site as string) ?? "waist"} onChange={(e) => set("site", e.target.value)}>
              {MEASUREMENT_SITES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <NumberField label="Measurement" k="valueIn" unit="inches" form={form} set={set} min={1} max={120} step={0.1} />
        </>
      );
    case "hunger":
      return (
        <>
          <ScaleField label="How hungry are you? (0 full – 10 starving)" k="hunger" form={form} set={set} min={0} max={10} />
          <div className="field">
            <label>Craving (optional)</label>
            <input className="input" value={(form.cravingType as string) ?? ""} onChange={(e) => set("cravingType", e.target.value)} placeholder="e.g. something sweet, salty, bored" />
          </div>
          <NotesField form={form} set={set} />
        </>
      );
  }
}
